import React from 'react';
import { Link, useLocation } from "react-router-dom";
import { Home, ChevronRight } from "lucide-react";
import { Helmet } from 'react-helmet-async';
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useLocale } from "@/hooks/useLocale";
import type { Locale } from "@/i18n/types";

interface BreadcrumbItem {
  label: string;
  path?: string;
}

interface BreadcrumbsWithSchemaProps {
  items?: BreadcrumbItem[];
  customLabels?: Record<string, string>;
  className?: string;
}

// UI chrome strings (Home link label / aria)
const CHROME: Record<Locale, { home: string }> = {
  es: { home: "Inicio" },
  de: { home: "Start" },
  fr: { home: "Accueil" },
  it: { home: "Home" },
  pt: { home: "Início" },
  en: { home: "Home" },
};

// Segment → localized label. Keys are the (mostly Spanish) URL slugs currently in use.
// Only user-facing public pages are translated; auth-only dashboard labels stay in ES.
const SEGMENT_LABELS: Record<Locale, Record<string, string>> = {
  es: {
    "quienes-somos": "Quiénes Somos", "servicios": "Servicios", "casos-exito": "Casos de Éxito",
    "blog": "Blog", "recursos": "Recursos", "faq": "Preguntas Frecuentes", "contacto": "Contacto",
    "mision-vision": "Misión y Visión", "valores": "Nuestros Valores", "equipo": "Nuestro Equipo",
    "carreras": "Trabaja con Nosotros",
    "gestoria-administracion": "Gestoría y Administración", "asesoria-legal": "Asesoría Legal",
    "cursos-idiomas": "Cursos de Idiomas", "traducciones-oficiales": "Traducciones Oficiales",
    "diseno-web-hosting": "Diseño Web y Hosting", "consultoria-seguros": "Consultoría de Seguros",
    "soluciones-digitales-avanzadas": "Soluciones Digitales Avanzadas",
    "creacion-empresas-suiza": "Creación de Empresas en Suiza", "planificacion-fiscal": "Planificación Fiscal",
    "tramites-laborales-sociales": "Trámites Laborales y Sociales", "gestiones-menores": "Gestiones Menores",
    "reagrupacion-familiar-visados": "Reagrupación Familiar y Visados",
    "derecho-laboral-contratos": "Derecho Laboral y Contratos",
    "derecho-familia-sucesiones": "Derecho de Familia y Sucesiones",
    "legalizaciones-apostillas": "Legalizaciones y Apostillas",
    "aleman-a1-c1": "Alemán (A1-C1)", "ingles-b1-c1": "Inglés (B1-C1)",
    "presencial": "Cursos Presenciales", "online": "Cursos Online", "clase-prueba": "Clase de Prueba",
    "documentos-legales": "Documentos Legales", "interprete-24h": "Intérprete 24h",
    "revision-terminologica": "Revisión Terminológica",
    "seguros-salud": "Seguros de Salud", "seguros-hogar-vehiculos": "Seguros de Hogar y Vehículos",
    "seguros-vida-pension": "Seguros de Vida y Pensión", "seguros-empresariales": "Seguros Empresariales",
    "whitepapers": "Whitepapers", "guias-practicas": "Guías Prácticas", "videos-webinars": "Videos y Webinars",
    "formulario": "Formulario de Contacto", "oficina-opfikon": "Oficina de Opfikon",
    "whatsapp-chat": "WhatsApp y Chat",
    "politica-privacidad": "Política de Privacidad", "aviso-legal": "Aviso Legal", "cookies": "Política de Cookies",
    "gestoria-fiscal": "Gestoría y Fiscal", "seguros-pensiones": "Seguros y Pensiones",
    "digital": "Soluciones Digitales", "legal": "Asesoría Legal", "idiomas": "Formación en Idiomas",
    "nosotros": "Sobre Nosotros", "politica-de-privacidad": "Política de Privacidad",
    "politica-de-cookies": "Política de Cookies",
    "login": "Iniciar Sesión", "registro": "Registrarse",
  },
  de: {
    "quienes-somos": "Über uns", "servicios": "Leistungen", "casos-exito": "Erfolgsgeschichten",
    "blog": "Blog", "recursos": "Ressourcen", "faq": "Häufige Fragen", "contacto": "Kontakt",
    "mision-vision": "Mission & Vision", "valores": "Unsere Werte", "equipo": "Unser Team",
    "carreras": "Karriere",
    "gestoria-administracion": "Verwaltung & Behördengänge", "asesoria-legal": "Rechtsberatung",
    "cursos-idiomas": "Sprachkurse", "traducciones-oficiales": "Offizielle Übersetzungen",
    "diseno-web-hosting": "Webdesign & Hosting", "consultoria-seguros": "Versicherungsberatung",
    "soluciones-digitales-avanzadas": "Digitale Lösungen",
    "creacion-empresas-suiza": "Firmengründung in der Schweiz", "planificacion-fiscal": "Steuerplanung",
    "tramites-laborales-sociales": "Arbeits- und Sozialverfahren", "gestiones-menores": "Kleine Behördengänge",
    "reagrupacion-familiar-visados": "Familiennachzug & Visa",
    "derecho-laboral-contratos": "Arbeitsrecht & Verträge",
    "derecho-familia-sucesiones": "Familien- & Erbrecht",
    "legalizaciones-apostillas": "Beglaubigungen & Apostillen",
    "aleman-a1-c1": "Deutsch (A1–C1)", "ingles-b1-c1": "Englisch (B1–C1)",
    "presencial": "Präsenzkurse", "online": "Online-Kurse", "clase-prueba": "Probestunde",
    "documentos-legales": "Rechtsdokumente", "interprete-24h": "Dolmetscher 24h",
    "revision-terminologica": "Terminologische Prüfung",
    "seguros-salud": "Krankenversicherungen", "seguros-hogar-vehiculos": "Haus- & Fahrzeugversicherungen",
    "seguros-vida-pension": "Lebens- & Pensionsversicherungen", "seguros-empresariales": "Firmenversicherungen",
    "whitepapers": "Whitepapers", "guias-practicas": "Praktische Leitfäden", "videos-webinars": "Videos & Webinare",
    "formulario": "Kontaktformular", "oficina-opfikon": "Büro Opfikon", "whatsapp-chat": "WhatsApp & Chat",
    "politica-privacidad": "Datenschutz", "aviso-legal": "Impressum", "cookies": "Cookie-Richtlinie",
    "gestoria-fiscal": "Verwaltung & Steuern", "seguros-pensiones": "Versicherungen & Renten",
    "digital": "Digitale Lösungen", "legal": "Rechtsberatung", "idiomas": "Sprachen",
    "nosotros": "Über uns", "politica-de-privacidad": "Datenschutz", "politica-de-cookies": "Cookie-Richtlinie",
    "login": "Anmelden", "registro": "Registrieren",
  },
  fr: {
    "quienes-somos": "À propos", "servicios": "Services", "casos-exito": "Cas de réussite",
    "blog": "Blog", "recursos": "Ressources", "faq": "Questions fréquentes", "contacto": "Contact",
    "mision-vision": "Mission & Vision", "valores": "Nos valeurs", "equipo": "Notre équipe",
    "carreras": "Carrières",
    "gestoria-administracion": "Gestion & Administration", "asesoria-legal": "Conseil juridique",
    "cursos-idiomas": "Cours de langues", "traducciones-oficiales": "Traductions officielles",
    "diseno-web-hosting": "Design web & Hébergement", "consultoria-seguros": "Conseil en assurances",
    "soluciones-digitales-avanzadas": "Solutions numériques",
    "creacion-empresas-suiza": "Création d'entreprises en Suisse", "planificacion-fiscal": "Planification fiscale",
    "tramites-laborales-sociales": "Démarches sociales & du travail", "gestiones-menores": "Petites démarches",
    "reagrupacion-familiar-visados": "Regroupement familial & Visas",
    "derecho-laboral-contratos": "Droit du travail & Contrats",
    "derecho-familia-sucesiones": "Droit de la famille & Successions",
    "legalizaciones-apostillas": "Légalisations & Apostilles",
    "aleman-a1-c1": "Allemand (A1–C1)", "ingles-b1-c1": "Anglais (B1–C1)",
    "presencial": "Cours en présentiel", "online": "Cours en ligne", "clase-prueba": "Cours d'essai",
    "documentos-legales": "Documents juridiques", "interprete-24h": "Interprète 24h",
    "revision-terminologica": "Révision terminologique",
    "seguros-salud": "Assurances santé", "seguros-hogar-vehiculos": "Assurances habitation & véhicules",
    "seguros-vida-pension": "Assurances vie & retraite", "seguros-empresariales": "Assurances entreprises",
    "whitepapers": "Livres blancs", "guias-practicas": "Guides pratiques", "videos-webinars": "Vidéos & Webinaires",
    "formulario": "Formulaire de contact", "oficina-opfikon": "Bureau d'Opfikon", "whatsapp-chat": "WhatsApp & Chat",
    "politica-privacidad": "Politique de confidentialité", "aviso-legal": "Mentions légales", "cookies": "Politique de cookies",
    "gestoria-fiscal": "Gestion & Fiscalité", "seguros-pensiones": "Assurances & Retraites",
    "digital": "Solutions numériques", "legal": "Conseil juridique", "idiomas": "Langues",
    "nosotros": "À propos", "politica-de-privacidad": "Politique de confidentialité", "politica-de-cookies": "Politique de cookies",
    "login": "Se connecter", "registro": "S'inscrire",
  },
  it: {
    "quienes-somos": "Chi siamo", "servicios": "Servizi", "casos-exito": "Casi di successo",
    "blog": "Blog", "recursos": "Risorse", "faq": "Domande frequenti", "contacto": "Contatti",
    "mision-vision": "Missione e Visione", "valores": "I nostri valori", "equipo": "Il nostro team",
    "carreras": "Lavora con noi",
    "gestoria-administracion": "Gestione & Amministrazione", "asesoria-legal": "Consulenza legale",
    "cursos-idiomas": "Corsi di lingua", "traducciones-oficiales": "Traduzioni ufficiali",
    "diseno-web-hosting": "Web design & Hosting", "consultoria-seguros": "Consulenza assicurativa",
    "soluciones-digitales-avanzadas": "Soluzioni digitali",
    "creacion-empresas-suiza": "Costituzione di società in Svizzera", "planificacion-fiscal": "Pianificazione fiscale",
    "tramites-laborales-sociales": "Pratiche del lavoro e sociali", "gestiones-menores": "Piccole pratiche",
    "reagrupacion-familiar-visados": "Ricongiungimento familiare & Visti",
    "derecho-laboral-contratos": "Diritto del lavoro & Contratti",
    "derecho-familia-sucesiones": "Diritto di famiglia & Successioni",
    "legalizaciones-apostillas": "Legalizzazioni & Apostille",
    "aleman-a1-c1": "Tedesco (A1–C1)", "ingles-b1-c1": "Inglese (B1–C1)",
    "presencial": "Corsi in presenza", "online": "Corsi online", "clase-prueba": "Lezione di prova",
    "documentos-legales": "Documenti legali", "interprete-24h": "Interprete 24h",
    "revision-terminologica": "Revisione terminologica",
    "seguros-salud": "Assicurazioni sanitarie", "seguros-hogar-vehiculos": "Assicurazioni casa & veicoli",
    "seguros-vida-pension": "Assicurazioni vita & pensione", "seguros-empresariales": "Assicurazioni aziendali",
    "whitepapers": "Whitepaper", "guias-practicas": "Guide pratiche", "videos-webinars": "Video & Webinar",
    "formulario": "Modulo di contatto", "oficina-opfikon": "Ufficio di Opfikon", "whatsapp-chat": "WhatsApp & Chat",
    "politica-privacidad": "Informativa sulla privacy", "aviso-legal": "Note legali", "cookies": "Politica dei cookie",
    "gestoria-fiscal": "Gestione & Fiscalità", "seguros-pensiones": "Assicurazioni & Pensioni",
    "digital": "Soluzioni digitali", "legal": "Consulenza legale", "idiomas": "Lingue",
    "nosotros": "Chi siamo", "politica-de-privacidad": "Informativa sulla privacy", "politica-de-cookies": "Politica dei cookie",
    "login": "Accedi", "registro": "Registrati",
  },
  pt: {
    "quienes-somos": "Quem somos", "servicios": "Serviços", "casos-exito": "Casos de sucesso",
    "blog": "Blog", "recursos": "Recursos", "faq": "Perguntas frequentes", "contacto": "Contacto",
    "mision-vision": "Missão e Visão", "valores": "Os nossos valores", "equipo": "A nossa equipa",
    "carreras": "Trabalha connosco",
    "gestoria-administracion": "Gestão & Administração", "asesoria-legal": "Assessoria jurídica",
    "cursos-idiomas": "Cursos de idiomas", "traducciones-oficiales": "Traduções oficiais",
    "diseno-web-hosting": "Design web & Hospedagem", "consultoria-seguros": "Consultoria de seguros",
    "soluciones-digitales-avanzadas": "Soluções digitais",
    "creacion-empresas-suiza": "Criação de empresas na Suíça", "planificacion-fiscal": "Planeamento fiscal",
    "tramites-laborales-sociales": "Trâmites laborais e sociais", "gestiones-menores": "Pequenos trâmites",
    "reagrupacion-familiar-visados": "Reagrupamento familiar & Vistos",
    "derecho-laboral-contratos": "Direito laboral & Contratos",
    "derecho-familia-sucesiones": "Direito da família & Sucessões",
    "legalizaciones-apostillas": "Legalizações & Apostilas",
    "aleman-a1-c1": "Alemão (A1–C1)", "ingles-b1-c1": "Inglês (B1–C1)",
    "presencial": "Cursos presenciais", "online": "Cursos online", "clase-prueba": "Aula experimental",
    "documentos-legales": "Documentos legais", "interprete-24h": "Intérprete 24h",
    "revision-terminologica": "Revisão terminológica",
    "seguros-salud": "Seguros de saúde", "seguros-hogar-vehiculos": "Seguros de casa & veículos",
    "seguros-vida-pension": "Seguros de vida & pensão", "seguros-empresariales": "Seguros empresariais",
    "whitepapers": "Whitepapers", "guias-practicas": "Guias práticos", "videos-webinars": "Vídeos & Webinars",
    "formulario": "Formulário de contacto", "oficina-opfikon": "Escritório de Opfikon", "whatsapp-chat": "WhatsApp & Chat",
    "politica-privacidad": "Política de privacidade", "aviso-legal": "Aviso legal", "cookies": "Política de cookies",
    "gestoria-fiscal": "Gestão & Fiscalidade", "seguros-pensiones": "Seguros & Pensões",
    "digital": "Soluções digitais", "legal": "Assessoria jurídica", "idiomas": "Idiomas",
    "nosotros": "Sobre nós", "politica-de-privacidad": "Política de privacidade", "politica-de-cookies": "Política de cookies",
    "login": "Iniciar sessão", "registro": "Registar",
  },
  en: {
    "quienes-somos": "About us", "servicios": "Services", "casos-exito": "Success stories",
    "blog": "Blog", "recursos": "Resources", "faq": "FAQ", "contacto": "Contact",
    "mision-vision": "Mission & Vision", "valores": "Our values", "equipo": "Our team",
    "carreras": "Careers",
    "gestoria-administracion": "Administration & Paperwork", "asesoria-legal": "Legal advice",
    "cursos-idiomas": "Language courses", "traducciones-oficiales": "Official translations",
    "diseno-web-hosting": "Web design & Hosting", "consultoria-seguros": "Insurance consulting",
    "soluciones-digitales-avanzadas": "Digital solutions",
    "creacion-empresas-suiza": "Company formation in Switzerland", "planificacion-fiscal": "Tax planning",
    "tramites-laborales-sociales": "Labour & social procedures", "gestiones-menores": "Minor procedures",
    "reagrupacion-familiar-visados": "Family reunification & Visas",
    "derecho-laboral-contratos": "Employment law & Contracts",
    "derecho-familia-sucesiones": "Family law & Inheritance",
    "legalizaciones-apostillas": "Legalisations & Apostilles",
    "aleman-a1-c1": "German (A1–C1)", "ingles-b1-c1": "English (B1–C1)",
    "presencial": "In-person courses", "online": "Online courses", "clase-prueba": "Trial class",
    "documentos-legales": "Legal documents", "interprete-24h": "24h interpreter",
    "revision-terminologica": "Terminology review",
    "seguros-salud": "Health insurance", "seguros-hogar-vehiculos": "Home & vehicle insurance",
    "seguros-vida-pension": "Life & pension insurance", "seguros-empresariales": "Business insurance",
    "whitepapers": "Whitepapers", "guias-practicas": "Practical guides", "videos-webinars": "Videos & Webinars",
    "formulario": "Contact form", "oficina-opfikon": "Opfikon office", "whatsapp-chat": "WhatsApp & Chat",
    "politica-privacidad": "Privacy policy", "aviso-legal": "Legal notice", "cookies": "Cookie policy",
    "gestoria-fiscal": "Administration & Tax", "seguros-pensiones": "Insurance & Pensions",
    "digital": "Digital solutions", "legal": "Legal advice", "idiomas": "Languages",
    "nosotros": "About us", "politica-de-privacidad": "Privacy policy", "politica-de-cookies": "Cookie policy",
    "login": "Log in", "registro": "Sign up",
  },
};

const BLOG_CATEGORY_PREFIX: Record<Locale, string> = {
  es: "Categoría", de: "Kategorie", fr: "Catégorie", it: "Categoria", pt: "Categoria", en: "Category",
};

const BreadcrumbsWithSchema: React.FC<BreadcrumbsWithSchemaProps> = ({
  items = [],
  customLabels = {},
  className = "",
}) => {
  const location = useLocation();
  const locale = useLocale();
  const chrome = CHROME[locale];

  const breadcrumbItems: BreadcrumbItem[] =
    items.length > 0 ? items : buildBreadcrumbFromPath(location.pathname, customLabels, locale);

  const schemaItems = breadcrumbItems.map((item, index) => ({
    name: item.label,
    url:
      item.path ||
      `${window.location.origin}${location.pathname.split('/').slice(0, index + 2).join('/')}`,
  }));

  if (schemaItems.length > 0 && !schemaItems[0].url.endsWith('/')) {
    schemaItems.unshift({ name: chrome.home, url: window.location.origin });
  }

  const schema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": schemaItems.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url,
    })),
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      <div className={`py-2 ${className}`}>
        <div className="container px-4">
          <Breadcrumb>
            <BreadcrumbList className="flex-wrap justify-start gap-1 text-xs sm:text-sm">
              <BreadcrumbItem>
                <BreadcrumbLink asChild>
                  <Link
                    to="/"
                    className="flex items-center text-muted-foreground/70 hover:text-foreground transition-colors"
                    aria-label={chrome.home}
                  >
                    <Home className="h-3.5 w-3.5 shrink-0" />
                  </Link>
                </BreadcrumbLink>
              </BreadcrumbItem>

              {breadcrumbItems.map((item, index) => (
                <span key={index} className="contents">
                  <BreadcrumbSeparator className="text-muted-foreground/40">
                    <ChevronRight className="h-3 w-3 shrink-0" />
                  </BreadcrumbSeparator>
                  <BreadcrumbItem>
                    {item.path && index !== breadcrumbItems.length - 1 ? (
                      <BreadcrumbLink asChild>
                        <Link
                          to={item.path}
                          className="text-muted-foreground/70 hover:text-foreground transition-colors truncate max-w-[120px] sm:max-w-[180px]"
                        >
                          {item.label}
                        </Link>
                      </BreadcrumbLink>
                    ) : (
                      <BreadcrumbPage className="text-foreground/80 font-medium truncate max-w-[150px] sm:max-w-[220px]">
                        {item.label}
                      </BreadcrumbPage>
                    )}
                  </BreadcrumbItem>
                </span>
              ))}
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </div>
    </>
  );
};

function buildBreadcrumbFromPath(
  path: string,
  customLabels: Record<string, string>,
  locale: Locale,
): BreadcrumbItem[] {
  const cleanPath = path.endsWith("/") ? path.slice(0, -1) : path;
  if (cleanPath === "") return [];

  const segments = cleanPath.split("/").filter(Boolean);
  const breadcrumbItems: BreadcrumbItem[] = [];
  let currentPath = "";

  // Skip the leading locale segment when building labels/paths.
  const startIndex = segments.length > 0 && (SEGMENT_LABELS as any)[segments[0]] === undefined && ["es","de","fr","it","pt","en"].includes(segments[0])
    ? 1
    : 0;

  for (let i = 0; i < segments.length; i++) {
    currentPath += `/${segments[i]}`;
    if (i < startIndex) continue;

    const label = formatBreadcrumbLabel(segments[i], currentPath, segments, i, customLabels, locale);
    breadcrumbItems.push({
      label,
      path: i === segments.length - 1 ? undefined : currentPath,
    });
  }

  return breadcrumbItems;
}

function formatBreadcrumbLabel(
  pathSegment: string,
  currentPath: string,
  segments: string[],
  index: number,
  customLabels: Record<string, string>,
  locale: Locale,
): string {
  if (customLabels[currentPath]) return customLabels[currentPath];
  if (customLabels[pathSegment]) return customLabels[pathSegment];

  const localized = SEGMENT_LABELS[locale][pathSegment] ?? SEGMENT_LABELS.es[pathSegment];
  if (localized) return localized;

  if (segments[0] === "blog" && segments[1] === "categoria" && index === 2) {
    return `${BLOG_CATEGORY_PREFIX[locale]}: ${formatTitleFromSlug(pathSegment)}`;
  }

  return formatTitleFromSlug(pathSegment);
}

function formatTitleFromSlug(slug: string): string {
  return slug
    .replace(/-/g, " ")
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

export default BreadcrumbsWithSchema;
